'use client';

import { classNames, cn } from '@commenda/utils';
import { useRouter } from 'next/navigation';
import * as React from 'react';
import { Button } from '../Button';
import { Dropdown, type DropdownMenuItemProps } from '../Dropdown';
import { Icons } from '../Icons';
import { Separator } from '../Separator';
import { Tooltip } from '../Tooltip';
import {
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  Breadcrumb as BreadcrumbRoot,
  BreadcrumbSeparator,
} from './_base';

export type BreadcrumbItemProps = {
  label?: string;
  icon?: React.ReactNode;
  /** When `null`, renders as the current `BreadcrumbPage` (non-interactive). */
  href: string | null;
  onClick?: () => void;
};

export type BreadcrumbPageMenu = {
  ariaLabel: string;
  items: DropdownMenuItemProps[];
};

export type BreadcrumbProps = {
  items: BreadcrumbItemProps[];
  _className?: string;
  limit?: number;
  onBack?: () => void;
  /** The page's own actions, behind a "⋯" button after the last crumb. */
  pageMenu?: BreadcrumbPageMenu;
  /** The page's primary actions, at the right end of the bar. */
  actions?: React.ReactNode;
};

const BreadcrumbLabel = ({ text }: { text: string }) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Tooltip content={text} disabled={!isTruncated} side="top" asChild>
      <span ref={ref} className="block truncate max-w-[240px]">
        {text}
      </span>
    </Tooltip>
  );
};

const BreadcrumbItemContent = ({ item }: { item: BreadcrumbItemProps }) => (
  <>
    {item.icon && (
      <span
        className={classNames(
          'flex items-center p-0.5',
          item.label ? 'mr-1.5' : ''
        )}
      >
        {item.icon}
      </span>
    )}
    {item.label && <BreadcrumbLabel text={item.label} />}
  </>
);

const renderItem = (item: BreadcrumbItemProps) => {
  const sharedClassName = 'flex items-center';
  const { onClick } = item;
  if (onClick) {
    return (
      <BreadcrumbLink
        href="#"
        onClick={(e) => {
          e.preventDefault();
          onClick();
        }}
        className={sharedClassName}
      >
        <BreadcrumbItemContent item={item} />
      </BreadcrumbLink>
    );
  }
  if (item.href) {
    return (
      <BreadcrumbLink href={item.href} className={sharedClassName}>
        <BreadcrumbItemContent item={item} />
      </BreadcrumbLink>
    );
  }
  return (
    <BreadcrumbPage className={sharedClassName}>
      <BreadcrumbItemContent item={item} />
    </BreadcrumbPage>
  );
};

const Breadcrumb = ({
  items,
  _className,
  limit = 5,
  onBack,
  pageMenu,
  actions,
}: BreadcrumbProps) => {
  const router = useRouter();

  const shouldCollapse = items.length > limit;

  const visibleItems: {
    first: BreadcrumbItemProps | null;
    collapsed: BreadcrumbItemProps[];
    last: BreadcrumbItemProps[];
  } = React.useMemo(() => {
    if (items.length === 0) {
      return { first: null, collapsed: [], last: [] };
    }
    if (!shouldCollapse) {
      return { first: null, collapsed: [], last: items };
    }
    return {
      first: items[0],
      collapsed: items.slice(1, items.length - (limit - 1)),
      last: items.slice(-(limit - 1)),
    };
  }, [items, limit, shouldCollapse]);

  return (
    <div
      className={cn(
        'flex items-center py-md px-xl border-b w-full h-11',
        _className
      )}
    >
      {onBack && (
        <>
          <Button
            onClick={onBack}
            variant="link"
            aria-label="Go back"
            className="transition-colors text-text-tertiary hover:text-text-quaternary-hover"
            size="sm"
          >
            <Icons.ArrowLeft className="size-4" />
            <span>Back</span>
          </Button>
          <Separator
            orientation="vertical"
            className="h-5 w-0.5 bg-fg-quinary ml-3 mr-2"
          />
        </>
      )}

      {/* Breadcrumb Navigation */}
      <BreadcrumbRoot className="flex-1">
        <BreadcrumbList className="items-center">
          {!shouldCollapse ? (
            // Render all items normally
            items.map((item, index) => (
              <React.Fragment key={item.label || index}>
                <BreadcrumbItem className="flex items-center">
                  {renderItem(item)}
                </BreadcrumbItem>
                {index < items.length - 1 && <BreadcrumbSeparator />}
              </React.Fragment>
            ))
          ) : (
            // Render: first item, ellipsis (covering middle items), trailing items.
            <>
              {visibleItems.first && (
                <>
                  <BreadcrumbItem className="flex items-center">
                    {renderItem(visibleItems.first)}
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                </>
              )}

              {visibleItems.collapsed.length > 0 && (
                <>
                  <BreadcrumbItem>
                    <Dropdown
                      menu={{
                        groups: [
                          {
                            items: visibleItems.collapsed.map((item) => ({
                              label: item.label ?? '',
                              onClick: () => {
                                if (item.onClick) {
                                  item.onClick();
                                } else if (item.href) {
                                  router.push(item.href);
                                }
                              },
                            })),
                          },
                        ],
                      }}
                      align="start"
                    >
                      <div className="p-0 m-0 cursor-pointer rounded-sm hover:bg-bg-secondary">
                        <BreadcrumbEllipsis className="text-text-quaternary" />
                      </div>
                    </Dropdown>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                </>
              )}

              {visibleItems.last.map((item, index) => (
                <React.Fragment key={item.label || index}>
                  <BreadcrumbItem className="flex items-center">
                    {renderItem(item)}
                  </BreadcrumbItem>
                  {index < visibleItems.last.length - 1 && (
                    <BreadcrumbSeparator />
                  )}
                </React.Fragment>
              ))}
            </>
          )}
          {pageMenu && (
            <BreadcrumbItem>
              <Dropdown
                menu={{ groups: [{ items: pageMenu.items }] }}
                align="start"
              >
                <button
                  type="button"
                  aria-label={pageMenu.ariaLabel}
                  className="flex size-7 items-center justify-center rounded-full border border-transparent p-sm text-text-tertiary transition-colors hover:border-border-primary hover:bg-bg-secondary hover:shadow-xs"
                >
                  <Icons.DotsHorizontal className="size-4" />
                </button>
              </Dropdown>
            </BreadcrumbItem>
          )}
        </BreadcrumbList>
      </BreadcrumbRoot>
      {actions ? (
        <div className="flex shrink-0 items-center gap-sm">{actions}</div>
      ) : null}
    </div>
  );
};

export default Breadcrumb;
